import React from "react";
import { Controller, useForm } from "react-hook-form";

import { FieldLabel, Icon, IconBox } from "@proda-ai/ui-kit";

import Box from "@/ui/atoms/Box";
import FormBadge from "@/ui/atoms/FormBadge";
import TextField from "@/ui/atoms/TextField";

import Dropdown from "@/ui/molecules/Dropdown";

import type {
    MultipropBasePropertyInfo,
    NewProperty,
    MultipropPropertyInfo,
} from "../dealTypes";
import { assetTypes, subAssetTypes } from "../dealTypes";

import { isString, isTruthyOrZero } from "@/utils/guards";
import { creationTestIds } from "./utils";

export type PropertyDetailsProps = {
    property?: Partial<MultipropPropertyInfo | NewProperty>;
    isCollapsed?: boolean;
    onSave: (data: Partial<MultipropPropertyInfo | NewProperty>) => void;
    onResidentialFieldsClear: () => void;
    onEdit: () => void;
    getCurrentDate: () => Date;
};

const ASSET_TYPE_TO_SUB_TYPES: Record<string, string[]> = {
    ATResidential: ["multifamily", "manufactured_housing"],
};

const PropertyDetailsForm: React.FC<PropertyDetailsProps> = ({
    property,
    isCollapsed = false,
    onSave,
    onResidentialFieldsClear,
    onEdit,
    getCurrentDate,
}) => {
    const {
        register,
        handleSubmit,
        watch,
        control,
        setValue,
        unregister,
        formState: { errors, isValid, isDirty },
    } = useForm<MultipropBasePropertyInfo>({
        defaultValues: {
            assetType: property?.assetType ?? "ATResidential",
            subAssetType: property?.subAssetType,
            residentialUnits: isTruthyOrZero(property?.residentialUnits)
                ? property?.residentialUnits
                : null,
            yearOfConstruction: isTruthyOrZero(property?.yearOfConstruction)
                ? property?.yearOfConstruction
                : null,
            yearOfRenovation: isTruthyOrZero(property?.yearOfRenovation)
                ? property?.yearOfRenovation
                : null,
            buildingType: property?.buildingType,
            numOfFloors: isTruthyOrZero(property?.numOfFloors)
                ? property?.numOfFloors
                : null,
            numOfBuildings: isTruthyOrZero(property?.numOfBuildings)
                ? property?.numOfBuildings
                : null,
            parkingUnits: isTruthyOrZero(property?.parkingUnits)
                ? property?.parkingUnits
                : null,
        },
        mode: "onTouched",
    });

    const watchedDetails = watch();
    const watchedAssetType = watch("assetType");
    const watchedSubAssetType = watch("subAssetType");

    const isResidential = watchedAssetType === "ATResidential";

    React.useEffect(() => {
        if (!watchedAssetType) return;

        if (watchedAssetType !== "ATResidential") {
            unregister(["residentialUnits", "subAssetType", "buildingType"]);
            onResidentialFieldsClear();
        } else {
            const allowedSubTypes =
                ASSET_TYPE_TO_SUB_TYPES[watchedAssetType] || [];

            // "other" mirrors Excelsior free text and is valid for any asset
            // type — never auto-clear it, or saving would wipe the original.
            if (
                watchedSubAssetType &&
                watchedSubAssetType !== "other" &&
                (allowedSubTypes.length === 0 ||
                    !allowedSubTypes.includes(watchedSubAssetType))
            ) {
                setValue("subAssetType", undefined);
            }
        }
    }, [
        watchedAssetType,
        watchedSubAssetType,
        setValue,
        unregister,
        onResidentialFieldsClear,
    ]);

    const getSubAssetTypeOptions = () => {
        if (!watchedAssetType) return [];
        const allowedSubTypes = ASSET_TYPE_TO_SUB_TYPES[watchedAssetType] || [];

        const options = allowedSubTypes.map(subType => ({
            value: subType,
            label: subAssetTypes[subType as keyof typeof subAssetTypes],
        }));
        // Present when the property's stored value is "other" (or it's the
        // current selection) so the label renders AND the user can switch back
        // to it after clicking another option — abandoning "Other" must not be
        // one-way, or the Excelsior free text becomes unrestorable mid-edit.
        return watchedSubAssetType === "other" ||
            property?.subAssetType === "other"
            ? [...options, { value: "other", label: subAssetTypes.other }]
            : options;
    };

    const subAssetTypeOptions = getSubAssetTypeOptions();

    const isFormPopulated =
        isDirty || property?.yearOfConstruction !== undefined;
    const formBadgeStatus =
        isFormPopulated && isValid && isCollapsed ? "valid" : "empty";

    const cardHeader = (
        <Box
            sx={{
                display: "flex",
                paddingBottom: "1rem",
                marginBottom: "1rem",
                borderBottom: "1px solid var(--proda-white-150)",
                justifyContent: "space-between",
            }}
        >
            <Box
                sx={{
                    fontSize: "0.8rem",
                    display: "flex",
                }}
            >
                <FormBadge
                    status={formBadgeStatus}
                    sx={{ marginRight: "0.4rem" }}
                />
                <Box
                    sx={{
                        fontWeight: "bold",
                        marginRight: "0.3rem",
                    }}
                >
                    Specifications
                </Box>
                (required)
            </Box>
            {isCollapsed && (
                <IconBox>
                    <Icon
                        icon="pen-line"
                        onClick={onEdit}
                        data-testid={
                            creationTestIds.propertyFormEditDetailsButton
                        }
                    />
                </IconBox>
            )}
        </Box>
    );

    const buildingTypeOptions = {
        "garden/lowRise": "Garden / Low-Rise",
        midRise: "Mid-Rise",
        highRise: "High-Rise",
        sfr: "Single Family Rental (SFR)",
    };

    if (isCollapsed) {
        const buildingType = watchedDetails.buildingType;
        const buildingTypeLabel = isString(buildingType)
            ? buildingTypeOptions[
                  buildingType as keyof typeof buildingTypeOptions
              ]
            : null;
        const details: Record<string, string | number | null | undefined> = {
            "Asset Type": watchedDetails.assetType
                ? assetTypes[watchedDetails.assetType]
                : null,
            "Sub Asset Type": watchedDetails.subAssetType
                ? subAssetTypes[watchedDetails.subAssetType]
                : null,
            "No. of Residential Units": watchedDetails.residentialUnits,
            "Year of Construction": watchedDetails.yearOfConstruction,
            "Year Renovated": watchedDetails.yearOfRenovation,
            "Building Type": buildingTypeLabel,
            "No. of Floors": watchedDetails.numOfFloors,
            "No. of Buildings": watchedDetails.numOfBuildings,
            "No. of Parking Spaces": watchedDetails.parkingUnits,
        };

        const populatedDetails = Object.entries(details).filter(([, value]) =>
            isTruthyOrZero(value),
        );

        return (
            <Box
                sx={{
                    maxWidth: "600px",
                    margin: "2rem auto",
                    backgroundColor: "var(--proda-white-110)",
                    padding: "1rem",
                }}
            >
                {cardHeader}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {populatedDetails.map(([key, value]) => (
                        <Box
                            key={key}
                            sx={{
                                display: "flex",
                                justifyContent: "left",
                                marginBottom: "0.5rem",
                            }}
                        >
                            <Box
                                sx={{
                                    marginRight: "0.3rem",
                                }}
                            >
                                {key}:
                            </Box>
                            <Box
                                sx={{
                                    fontWeight: "bold",
                                }}
                            >
                                {value}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                maxWidth: "600px",
                margin: "2rem auto",
                backgroundColor: "var(--proda-white-110)",
                padding: "1rem",
            }}
        >
            {cardHeader}
            <form onSubmit={(...args) => void handleSubmit(onSave)(...args)}>
                <Box sx={{ marginBottom: "1rem" }}>
                    <Controller
                        control={control}
                        name="assetType"
                        rules={{
                            required: "Asset type is required",
                        }}
                        render={({ field }) => (
                            <Dropdown
                                id="assetType"
                                label="Asset Type*"
                                options={Object.entries(assetTypes).map(
                                    ([value, label]) => ({
                                        value,
                                        label,
                                    }),
                                )}
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                    {errors.assetType &&
                        errors.assetType.message !== undefined && (
                            <FieldLabel
                                label={errors.assetType.message}
                                mt={1}
                                fieldId="assetType"
                                hasErrors={true}
                            />
                        )}
                </Box>
                {isResidential && (
                    <Box sx={{ marginBottom: "1rem" }}>
                        <Controller
                            control={control}
                            name="subAssetType"
                            render={({ field }) => (
                                <Dropdown
                                    id="subAssetType"
                                    label="Sub Asset Type"
                                    placeholder={
                                        subAssetTypeOptions.length > 0
                                            ? "Select a Sub Asset Type"
                                            : "No sub asset types available"
                                    }
                                    options={subAssetTypeOptions}
                                    value={field.value ?? ""}
                                    onChange={field.onChange}
                                    disabled={subAssetTypeOptions.length === 0}
                                />
                            )}
                        />
                    </Box>
                )}
                {isResidential && (
                    <Box sx={{ marginBottom: "1rem" }}>
                        <TextField
                            id="residentialUnits"
                            label="No. of Residential Units*"
                            type="number"
                            {...register("residentialUnits", {
                                required:
                                    "Number of residential units is required",
                                min: {
                                    value: 0,
                                    message:
                                        "Number of residential units must be greater than or equal to 0",
                                },
                                max: {
                                    value: 100000,
                                    message:
                                        "Number of residential units must be less than or equal to 100,000",
                                },
                                valueAsNumber: true,
                            })}
                            placeholder="No. of Residential Units"
                            min="0"
                            hasErrors={!!errors.residentialUnits}
                            errorMessage={errors.residentialUnits?.message}
                            data-1p-ignore
                            autoComplete="off"
                        />
                    </Box>
                )}
                <Box sx={{ marginBottom: "1rem" }}>
                    <TextField
                        label="Year of Construction*"
                        type="number"
                        id="yearOfConstruction"
                        {...register("yearOfConstruction", {
                            required: "Year of construction is required",
                            min: {
                                value: 1400,
                                message: "Year of construction is invalid",
                            },
                            max: {
                                value: getCurrentDate().getFullYear(),
                                message: "Year of construction is invalid",
                            },
                            valueAsNumber: true,
                        })}
                        placeholder="Year of Construction"
                        min="0"
                        hasErrors={!!errors.yearOfConstruction}
                        errorMessage={errors.yearOfConstruction?.message}
                        data-1p-ignore
                        autoComplete="off"
                    />
                </Box>
                <Box sx={{ marginBottom: "1rem" }}>
                    <TextField
                        label="Year Renovated"
                        type="number"
                        id="yearOfRenovation"
                        {...register("yearOfRenovation", {
                            min: {
                                value: Math.max(
                                    1400,
                                    watchedDetails.yearOfConstruction ?? 1400,
                                ),
                                message: "Year Renovated is invalid",
                            },
                            max: {
                                value: getCurrentDate().getFullYear(),
                                message: "Year Renovated is invalid",
                            },
                            valueAsNumber: true,
                        })}
                        placeholder="Year Renovated"
                        min="0"
                        hasErrors={!!errors.yearOfRenovation}
                        errorMessage={errors.yearOfRenovation?.message}
                        data-1p-ignore
                        autoComplete="off"
                    />
                </Box>
                {isResidential && (
                    <Box sx={{ marginBottom: "1rem" }}>
                        <Controller
                            control={control}
                            name="buildingType"
                            render={({ field }) => (
                                <Dropdown
                                    id="buildingType"
                                    label="Building Type"
                                    placeholder="Select a Building Type"
                                    options={Object.entries(
                                        buildingTypeOptions,
                                    ).map(([value, label]) => ({
                                        value,
                                        label,
                                    }))}
                                    value={field.value ?? ""}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                        {errors.buildingType &&
                            errors.buildingType.message !== undefined && (
                                <FieldLabel
                                    label={errors.buildingType.message}
                                    mt={1}
                                    fieldId="buildingType"
                                    hasErrors={true}
                                />
                            )}
                    </Box>
                )}
                <Box sx={{ marginBottom: "1rem" }}>
                    <TextField
                        label="No. of Buildings"
                        type="number"
                        id="numOfBuildings"
                        {...register("numOfBuildings", {
                            min: {
                                value: 0,
                                message:
                                    "Number of Buildings must be greater than or equal to 0",
                            },
                            max: {
                                value: 5000,
                                message:
                                    "Number of Buildings must be less than or equal to 5,000",
                            },
                            valueAsNumber: true,
                        })}
                        placeholder="No. of Buildings"
                        min="0"
                        hasErrors={!!errors.numOfBuildings}
                        errorMessage={errors.numOfBuildings?.message}
                        data-1p-ignore
                        autoComplete="off"
                    />
                </Box>
                <Box sx={{ marginBottom: "1rem" }}>
                    <TextField
                        label="No. of Floors"
                        type="number"
                        id="numOfFloors"
                        {...register("numOfFloors", {
                            min: {
                                value: 0,
                                message:
                                    "Number of floors must be greater than or equal to 0",
                            },
                            max: {
                                value: 200,
                                message:
                                    "Number of floors must be less than or equal to 200",
                            },
                            valueAsNumber: true,
                        })}
                        placeholder="No. of Floors"
                        min="0"
                        hasErrors={!!errors.numOfFloors}
                        errorMessage={errors.numOfFloors?.message}
                        data-1p-ignore
                        autoComplete="off"
                    />
                </Box>
                <Box sx={{ marginBottom: "1rem" }}>
                    <TextField
                        label="No. of Parking Spaces"
                        type="number"
                        id="parkingUnits"
                        {...register("parkingUnits", {
                            min: {
                                value: 0,
                                message:
                                    "Number of parking spaces must be greater than or equal to 0",
                            },
                            max: {
                                value: 100000,
                                message:
                                    "Number of parking spaces must be less than or equal to 100,000",
                            },
                            valueAsNumber: true,
                        })}
                        placeholder="No. of Parking Spaces"
                        min="0"
                        hasErrors={!!errors.parkingUnits}
                        errorMessage={errors.parkingUnits?.message}
                        data-1p-ignore
                        autoComplete="off"
                    />
                </Box>

                <Box
                    component="button"
                    type="submit"
                    sx={{
                        padding: "0.5rem 2rem",
                        border: "none",
                        borderRadius: "4px",
                        marginTop: "1rem",
                        backgroundColor: "var(--primary-color)",
                        color: "var(--proda-white)",
                        cursor: "pointer",
                    }}
                    data-testid={
                        creationTestIds.propertyFormConfirmDetailsButton
                    }
                >
                    Confirm Specifications
                </Box>
            </form>
        </Box>
    );
};

export default PropertyDetailsForm;
